import React, { createContext, useState, useEffect } from "react";
import { Auth } from "aws-amplify";
import { useLocation } from "react-router-dom";

const AuthContext = createContext();

const useAuth = () => {
  const context = React.useContext(AuthContext);
  if (context === undefined) {
    throw new Error(
      "`useAuth` hook must be used within a `AuthContextProvider` component"
    );
  }
  return context;
};

const AuthContextProvider = ({ children }) => {
  let [authenticatedUser, setAuthenticatedUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  let location = useLocation();

  useEffect(() => {
    Auth.currentAuthenticatedUser()
      .then((user) => {
        setAuthenticatedUser(user);
      })
      .catch(() => {
        setAuthenticatedUser(null);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [location]);

  const confirmSignUp = async (username, code) => {
    const result = {
      error: undefined,
      response: {},
    };
    try {
      result.response = await Auth.confirmSignUp(username, code);
      console.log(result.response);
    } catch (error) {
      console.log("error confirmSignUp:", error);
      result.error = error;
    }
    return result;
  };

  const resendSignUp = async (username) => {
    const result = {
      error: undefined,
      response: {},
    };
    try {
      result.response = await Auth.resendSignUp(username);
    } catch (error) {
      console.log("error resendSignUp:", error);
      result.error = error;
    }
    return result;
  };

  const signIn = async (usernameOrAlias, password) => {
    const cognitoUser = await Auth.signIn(usernameOrAlias, password);
    setAuthenticatedUser(cognitoUser);
  };

  const signOut = async () => {
    await Auth.signOut();
    setAuthenticatedUser(null);
  };

  const deleteUser = async () => {
    if (!authenticatedUser) {
      // User is not authenticated
      return;
    }

    return new Promise((resolve, reject) => {
      authenticatedUser.deleteUser((error) => {
        if (error) {
          return reject(error);
        }
        setAuthenticatedUser(null);
        resolve();
      });
    });
  };

  const changePassword = async (oldPassword, newPassword) => {
    const user = await Auth.currentAuthenticatedUser();
    await Auth.changePassword(user, oldPassword, newPassword);
  };

  const updateUsername = async (newUsername) => {
    try {
      const user = await Auth.currentAuthenticatedUser();
      await Auth.updateUserAttributes(user, {
        preferred_username: newUsername,
      });
    } catch (error) {
      console.error("Error updating username:", error);
    }
  };

  return (
    <AuthContext.Provider
      value={{
        signIn,
        signOut,
        authenticatedUser,
        confirmSignUp,
        resendSignUp,
        deleteUser,
        changePassword,
        updateUsername,
        isLoading,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export { AuthContextProvider, useAuth };
